import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuctionMapper } from '../../mappers/auction.mapper';
import { Auction } from '@admin-shared/models/auction';
import { Foundation } from '../../models/foundation';
import { Team } from '../../models/team';
import { StorageParameters } from '../storage.service';
import { ApiBase } from './api.base';
import { AuctionResultState } from '../../enum/auction-result-state.enum';
import { AuctionState } from '../../enum/auction-state.enum';
import { Venue } from '../../models/venue';
import { FoundationPartner } from '../../models/foundation-partner';
import { FoundationPartnerMapper } from '../../mappers/foundation-partner.mapper';
import { AuctionAsset } from '@admin-shared/models/auction-asset';
import { Transaction } from '../../models/transaction';
import { Announcement } from '@admin-shared/models/announcements';
import { FulfillmentOptionType } from '@admin-shared/models/fulfillment-option';
export class FoundationsApi extends ApiBase {
  model = 'foundations';

  public create(data: Foundation): Observable<Foundation> {
    return this.http.post<Foundation>(this.buildUrl(), data, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public find(): Observable<Foundation[]> {
    return this.http.get<Foundation[]>(this.buildUrl(), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public findById(id: string): Observable<Foundation> {
    return this.http.get<Foundation>(this.buildUrl(id), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public deleteById(id: string) {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling foundationDeleteById.'
      );
    }
    return this.http.delete(this.buildUrl(id), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public patch(id: string, data?: Foundation): Observable<Foundation> {
    return this.http.patch<Foundation>(this.buildUrl(id), data, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public patchContact(id: string, data?: Foundation): Observable<Foundation> {
    return this.http.patch<Foundation>(this.buildUrl(id, 'contact'), data, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public createAuction(
    foundationId: string,
    data?: Auction
  ): Observable<Auction> {
    return this.http.post<Auction>(
      this.buildUrl(foundationId, 'auctions'),
      data,
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getAuctions(
    foundationId: string,
    options: {
      skip?: string;
      limit?: string;
      order?: string;
      q?: string;
      state?: AuctionState | AuctionState[];
      items?: 'true';
    } = {}
  ) {
    const params = new HttpParams({ fromObject: options });
    return this.http
      .get<Auction[]>(this.buildUrl(foundationId, 'auctions'), {
        params,
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      })
      .pipe(map(AuctionMapper.mapItems));
  }

  public getTotalAuctions(
    foundationId: string,
    options: {
      q?: string;
      state?: AuctionState | AuctionState[];
    } = {}
  ) {
    if (!foundationId) throw 'Foundation Id required';
    const params = new HttpParams({ fromObject: options });
    return this.http.get<{ total: number }>(
      this.buildUrl(foundationId, 'auctions', 'total'),
      {
        params,
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getTeam(id: string) {
    return this.http.get<Team | null>(this.buildUrl(id, 'team'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public getAddresses(id: string) {
    return this.http.get<{
      taxIntegration: boolean;
      nexusAddresses: any[] | undefined;
    }>(this.buildUrl(id, 'nexus'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public saveAddresses(id: string, nexusAddresses: any[]) {
    return this.http.patch<any[] | undefined>(
      this.buildUrl(id, 'nexus'),
      nexusAddresses,
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getAuctionResults(
    foundationId: string,
    options: {
      skip?: number;
      limit?: string;
      order?: string;
      q?: string;
      state?: AuctionResultState | AuctionResultState[];
      fulfillmentOptionType?: FulfillmentOptionType;
    } = {}
  ) {
    const params = new HttpParams({ fromObject: options });
    return this.http.get<{ total: number; items: Auction[] }>(
      this.buildUrl(foundationId, 'auction-results'),
      {
        params,
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getTotalAuctionResults(
    foundationId: string,
    options: {
      q?: string;
      state?: AuctionResultState | AuctionResultState[];
    } = {}
  ) {
    const params = new HttpParams({ fromObject: options });
    return this.http.get<{ total: number }>(
      this.buildUrl(foundationId, 'auction-results', 'total'),
      {
        params,
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getTransactions(foundationId: string, from: string, to: string) {
    return this.http.get<Transaction[]>(
      this.buildUrl(foundationId, 'transactions'),
      {
        params: {
          from,
          to,
        },
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public setTaxIntegration(foundationId: string, taxIntegration: boolean) {
    return this.http.post<Foundation>(
      this.buildUrl(foundationId, 'tax-integration'),
      {
        taxIntegration,
      },
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getVenues(foundationId: string) {
    return this.http.get<Venue[]>(this.buildUrl(foundationId, 'venues'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public hasPendingResults(foundationId: string) {
    return this.http.get<boolean>(
      this.buildUrl(foundationId, 'has-pending-results'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getPartners(foundationId: string) {
    return this.http
      .get<FoundationPartner[]>(this.buildUrl(foundationId, 'partners'), {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      })
      .pipe(map(FoundationPartnerMapper.mapItems));
  }

  public getSTHTemplate(id: string): Observable<Array<string[]>> {
    return this.http.get<Array<string[]>>(this.buildUrl(id, 'sth-template'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public getSTHFile(id: string): Observable<AuctionAsset> {
    return this.http.get<AuctionAsset>(this.buildUrl(id, 'sth-file'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public deleteSTHFile(id: string) {
    return this.http.delete<{ success: boolean }>(
      this.buildUrl(id, 'sth-file'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public validateSTHFile(id: string, fileName: string) {
    return this.http.get<{ isValid: boolean; notContainUsers?: boolean }>(
      this.buildUrl(id, 'validate-sth-file', fileName),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public cloneAuction(
    foundationId: string,
    data?: Auction
  ): Observable<Auction> {
    return this.http.post<Auction>(
      this.buildUrl(foundationId, 'auctions', 'clone'),
      data,
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public getAnnouncements(
    foundationId: string,
  ) {
    return this.http.get<Announcement[]>(
      this.buildUrl(foundationId, 'announcements'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public orderAuctions(id: string, auctions: string[], state: AuctionState): Observable<{completed: boolean}> {
    return this.http.post<{completed: boolean}>(this.buildUrl(id, 'auctions', 'order'), {auctions, state}, {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
    });
  }

  public getTeamQR(id: string) {
    return this.http.get<any>(this.buildUrl(id, 'qr'), {
      headers: this.setHeaders({
        Authorization: this.getStorage().getItem(
          StorageParameters.AccessTokenId
        ),
      }),
      observe: 'response',
      responseType: 'blob' as 'json',
    });
  }

  public getAuctionsOfAuctionResults(
    foundationId: string
  ) {
    return this.http.get<{
      state: AuctionResultState;
      fulfillmentType: FulfillmentOptionType;
      auctions: {
          _id: string;
          title: string;
      }[];
  }[]>(
      this.buildUrl(foundationId, 'auction-results', 'auctions-list'),
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }

  public downloadAuctionResults(
    foundationId: string,
    body: {[x:string]: {state: AuctionResultState[], fulfillmentType: FulfillmentOptionType[]}}
  ) {
    return this.http.post<Array<(string | number)[]>>(
      this.buildUrl(foundationId, 'auction-results', 'download'),
      body,
      {
        headers: this.setHeaders({
          Authorization: this.getStorage().getItem(
            StorageParameters.AccessTokenId
          ),
        }),
      }
    );
  }
}
